import { motion } from "framer-motion";
import {
  Database,
  Brain,
  Workflow,
  Rocket,
  LineChart,
  Headphones,
  ServerCog,
  GitBranch,
  Wrench,
  BookOpen,
  ShieldCheck,
} from "lucide-react";

const flow = [
  { icon: Database, label: "Data" },
  { icon: Brain, label: "AI Agent" },
  { icon: Workflow, label: "Decision" },
  { icon: Rocket, label: "Action" },
  { icon: LineChart, label: "Insights" },
];

const agents = [
  { icon: Headphones, title: "Customer Support Agents" },
  { icon: ServerCog, title: "IT Operations Agents" },
  { icon: GitBranch, title: "DevOps Agents" },
  { icon: ShieldCheck, title: "Industrial Monitoring Agents" },
  { icon: Wrench, title: "Predictive Maintenance Agents" },
  { icon: BookOpen, title: "Knowledge Assistants" },
];

const MESH =
  "https://static.prod-images.emergentagent.com/jobs/7f7f59f9-b87b-4610-b385-48ba29eca9db/images/e4ecddc888241ac8cbc37dca549522dc1b1f28c5c77eee00bab7163e61323311.png";

export default function AIAgents() {
  return (
    <section id="ai-agents" data-testid="ai-agents-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <img src={MESH} alt="" className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F19] via-[#0B0F19]/85 to-[#0B0F19]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-cyan-400 font-semibold mb-3">
            AI Agent Solutions
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Next-generation AI Agents for
            <span className="gradient-text-vivid"> business automation</span>
          </h2>
          <p className="mt-4 text-slate-300 text-base sm:text-lg">
            Autonomous, observable, secure. Built to plug into your data, your tools, and your SOPs.
          </p>
        </motion.div>

        {/* Workflow */}
        <div className="mt-14">
          <div className="relative flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4">
            {flow.map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={step.label} className="flex items-center gap-4 lg:flex-1">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.12, duration: 0.4 }}
                    className="relative flex-1 glass border border-white/10 rounded-2xl p-5 text-center"
                  >
                    <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 grid place-items-center shadow-lg shadow-cyan-500/20">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="mt-3 font-heading font-semibold">{step.label}</div>
                  </motion.div>
                  {i < flow.length - 1 && (
                    <motion.div
                      initial={{ opacity: 0, scaleX: 0 }}
                      whileInView={{ opacity: 1, scaleX: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.12 + 0.1, duration: 0.4 }}
                      className="hidden lg:block w-10 h-[2px] origin-left bg-gradient-to-r from-cyan-400/80 to-transparent"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Agents grid */}
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {agents.map((a, i) => {
            const Icon = a.icon;
            return (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="card-hover flex items-center gap-4 bg-[#131C2F] border border-white/10 rounded-2xl p-5"
              >
                <div className="w-11 h-11 rounded-xl bg-cyan-500/10 border border-cyan-500/20 grid place-items-center">
                  <Icon className="w-5 h-5 text-cyan-400" />
                </div>
                <div className="font-heading font-semibold">{a.title}</div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
